"use client"

import moment from "moment"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { ROUTES } from "../../routes/routeKeys"
import Plus from "../../assets/svgs/Plus.svg"
import PropertyFilter from "../../components/FilterComponents/PropertyFilter"
import ImportExport from "../../components/ImportExport"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import { BASE_URL } from "../../utils/Element"
import { initialPropertyFilterData } from "../../utils/initialData"
import { formatPhoneNumber } from "../../utils/utils"
import "./Properties.css"

const PropertiesDemo = () => {
  const [config] = useAuth()
  const navigate = useNavigate()
  const [tagsss, setTagsss] = useState([])
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [loadingOwner, setLoadingOwner] = useState(true)
  const [selectedItem, setSelectedItem] = useState([])
  const [propertylistingdata, setPropertylistingdata] = useState([])
  const [ownerlistingData, setOwnerlistingData] = useState([])
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)

  // DataTables refs
  const propertiesTableRef = useRef(null)
  const ownersTableRef = useRef(null)
  const propertiesDataTableRef = useRef(null)
  const ownersDataTableRef = useRef(null)

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialPropertyFilterData
  })

  const queryParams = new URLSearchParams(location.search)
  const [search, setSearch] = useState(queryParams.get("search") ?? "")

  // Initialize DataTables
  useEffect(() => {
    // Load jQuery and DataTables if not already loaded
    if (typeof window.$ === "undefined") {
      const script = document.createElement("script")
      script.src = "https://code.jquery.com/jquery-3.6.0.min.js"
      script.onload = () => {
        loadDataTables()
      }
      document.head.appendChild(script)
    } else {
      loadDataTables()
    }

    return () => {
      // Cleanup DataTables on unmount
      if (propertiesDataTableRef.current) {
        propertiesDataTableRef.current.destroy()
      }
      if (ownersDataTableRef.current) {
        ownersDataTableRef.current.destroy()
      }
    }
  }, [])

  const loadDataTables = () => {
    if (typeof window.$ === "undefined") return

    // Load DataTables CSS and JS
    const cssLink = document.createElement("link")
    cssLink.rel = "stylesheet"
    cssLink.href = "https://cdn.datatables.net/1.13.7/css/jquery.dataTables.min.css"
    document.head.appendChild(cssLink)

    const dtScript = document.createElement("script")
    dtScript.src = "https://cdn.datatables.net/1.13.7/js/jquery.dataTables.min.js"
    dtScript.onload = () => {
      initializeDataTables()
    }
    document.head.appendChild(dtScript)
  }

  const initializeDataTables = () => {
    const $ = window.$
    if (!$ || !$.fn.DataTable) {
      console.error("jQuery or DataTables not loaded properly")
      return
    }


    // Custom DataTables styling with Tailwind classes
    const customDataTablesConfig = {
      dom: '<"flex flex-col sm:flex-row justify-between items-center mb-4"<"flex items-center"l><"flex items-center"f>>rtip',
      language: {
        lengthMenu: "Show _MENU_ entries",
        search: "Search:",
        info: "Showing _START_ to _END_ of _TOTAL_ entries",
        infoEmpty: "Showing 0 to 0 of 0 entries",
        infoFiltered: "(filtered from _MAX_ total entries)",
        paginate: {
          first: "First",
          last: "Last",
          next: "Next",
          previous: "Previous",
        },
      },
      initComplete: () => {
        // Apply Tailwind classes to DataTables elements
        $(".dataTables_length select").addClass(
          "px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500",
        )
        $(".dataTables_filter input").addClass(
          "px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ml-2",
        )
        $(".dataTables_paginate .paginate_button").addClass(
          "px-3 py-1 mx-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors",
        )
        $(".dataTables_paginate .paginate_button.current").addClass(
          "bg-green-500 text-white border-green-500 hover:bg-green-600",
        )
        $(".dataTables_paginate .paginate_button.disabled").addClass(
          "text-gray-400 cursor-not-allowed hover:bg-transparent",
        )
      },
    }

    // Initialize Properties DataTable
    if (propertiesTableRef.current && !propertiesDataTableRef.current) {
      try {
        propertiesDataTableRef.current = $(propertiesTableRef.current).DataTable({
          ...customDataTablesConfig,
          processing: true,
          serverSide: true,
          searching: true,
          ordering: true,
          paging: true,
          pageLength: 50,
          lengthMenu: [
            [10, 25, 50, 100],
            [10, 25, 50, 100],
          ],
          ajax: {
            url: `${BASE_URL}/property-list`,
            type: "GET",
            headers: config.headers,
            data: (d) => {
              const params = {
                ...d,
                ...buildDataTablesParams(d, filterData),
                type: "property",
              }
             
              return params
            },
            dataSrc: (json) => {
     
              setPropertylistingdata(json.properties?.data || [])
              return json.properties?.data || []
            },
            error: (xhr, error, code) => {
              console.error("Properties DataTable Ajax Error:", {
                xhr: xhr,
                error: error,
                code: code,
                status: xhr.status,
                statusText: xhr.statusText,
                responseText: xhr.responseText,
              })
            },
          },
          columns: [
            {
              data: null,
              orderable: false,
              className: "w-12 text-center",
              render: (data, type, row) => `
                <label class="inline-flex items-center">
                  <input type="checkbox" class="property-checkbox w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2" data-id="${row.id}">
                </label>`,
            },
            {
              data: "property_name",
              className: "font-medium text-green-600 hover:text-green-700",
              render: (data, type, row) =>
                `<a href="/property/${row.id}?search=${search}" target="_blank" class="font-medium text-green-600 hover:text-green-700 hover:underline">${data || ""}</a>`,
            },
            {
              data: "property_type",
              className: "text-gray-600 capitalize",
              render: (data, type, row) => `<span class="text-gray-600 capitalize">${data?.type || ""}</span>`,
            },
            {
              data: "street_address",
              className: "text-gray-900",
            },
            {
              data: "city",
              className: "text-gray-900 capitalize",
              render: (data, type, row) => `<span class="capitalize">${data || ""}</span>`,
            },
            {
              data: "state",
              className: "text-gray-900",
            },
            {
              data: "owner",
              className: "font-medium text-green-600 hover:text-green-700",
              render: (data, type, row) => {
                if (!data?.name) return ""
                const url = row.owner_company_id
                  ? `/FilterData/${row.owner_company_id}/company`
                  : `/FilterData/${row.owner_contact_id}/contact`
                return `<a href="${url}" target="_blank" class="font-medium text-green-600 hover:text-green-700 hover:underline capitalize">${data.name}</a>`
              },
            },
            {
              data: null,
              className: "text-gray-600 text-sm",
              render: (data, type, row) => {
                let dateConnected = ""
                let fromNow = ""

                if (row.owner_contact_id && row.contact?.latest_activity_including_contacts?.date_connected) {
                  const date = moment(row.contact.latest_activity_including_contacts.date_connected).tz(
                    "America/New_York",
                  )
                  dateConnected = date.format("MM/DD/YYYY")
                  fromNow = ` <span class="text-xs text-gray-500">(${date.fromNow()})</span>`
                } else if (row.owner_company_id && row.company?.latest_activity_including_contacts?.date_connected) {
                  const date = moment(row.company.latest_activity_including_contacts.date_connected).tz(
                    "America/New_York",
                  )
                  dateConnected = date.format("MM/DD/YYYY")
                  fromNow = ` <span class="text-xs text-gray-500">(${date.fromNow()})</span>`
                }

                return `<span class="text-gray-600">${dateConnected}${fromNow}</span>`
              },
            },
            {
              data: "updated_at",
              className: "text-gray-600",
              render: (data, type, row) =>
                `<span class="text-gray-600">${moment(data).tz("America/New_York").format("MM/DD/YYYY")}</span>`,
            },
            {
              data: "propertytag_details",
              orderable: false,
              className: "max-w-xs",
              render: (data, type, row) => {
                if (!data || !Array.isArray(data)) return ""
                return data
                  .map(
                    (tag) =>
                      `<span class="inline-block px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full mr-1 mb-1">${tag.tag_name}</span>`,
                  )
                  .join("")
              },
            },
          ],
          drawCallback: () => {
            // Handle checkbox events
            $(".property-checkbox")
              .off("change")
              .on("change", function () {
                const id = Number.parseInt($(this).data("id"))
                const isChecked = $(this).is(":checked")

                if (isChecked) {
                  if (!selectedItem.find((item) => item.id === id)) {
                    const property = propertylistingdata.find((p) => p.id === id)
                    if (property) {
                      setSelectedItem((prev) => [...prev, property])
                    }
                  }
                } else {
                  setSelectedItem((prev) => prev.filter((item) => item.id !== id))
                }
              })

            // Handle select all
            const selectAllCheckbox = $("#properties-select-all")
            const checkboxes = $(".property-checkbox")
            const checkedCount = checkboxes.filter(":checked").length

            selectAllCheckbox.prop("indeterminate", checkedCount > 0 && checkedCount < checkboxes.length)
            selectAllCheckbox.prop("checked", checkedCount === checkboxes.length && checkboxes.length > 0)
          },
        })
       
      } catch (error) {
        console.error("Error initializing Properties DataTable:", error)
      }
    }

    // Initialize Owners DataTable
    if (ownersTableRef.current && !ownersDataTableRef.current) {
      try {
        ownersDataTableRef.current = $(ownersTableRef.current).DataTable({
          ...customDataTablesConfig,
          processing: true,
          serverSide: true,
          searching: true,
          ordering: true,
          paging: true,
          pageLength: 10,
          lengthMenu: [
            [10, 25, 50, 100],
            [10, 25, 50, 100],
          ],
          ajax: {
            url: `${BASE_URL}/property-owner-list`,
            type: "GET",
            headers: config.headers,
            data: (d) => {
              const params = {
                ...d,
                ...buildDataTablesParams(d, filterData),
                type: "property",
              }
              
              return params
            },
            dataSrc: (json) => {
             
              setOwnerlistingData(json.data?.properties?.data || [])
              return json.data?.properties?.data || []
            },
            error: (xhr, error, code) => {
              console.error("Owners DataTable Ajax Error:", {
                xhr: xhr,
                error: error,
                code: code,
                status: xhr.status,
                statusText: xhr.statusText,
                responseText: xhr.responseText,
              })
            },
          },
          columns: [
            {
              data: "owner_name",
              className: "font-medium text-gray-900",
              render: (data, type, row) => {
                const url = row.owner_company_id
                  ? `/FilterData/${row.owner_company_id}/company`
                  : `/FilterData/${row.owner_contact_id}/contact`
                return `<a href="${url}" target="_blank" class="font-medium text-gray-900 hover:text-green-600 hover:underline">${data || ""}</a>`
              },
            },
            {
              data: "owner_info_type",
              className: "text-gray-600 capitalize",
              render: (data, type, row) => `<span class="text-gray-600 capitalize">${data || ""}</span>`,
            },
            {
              data: "owner_phone",
              className: "text-gray-900",
              render: (data, type, row) => (data && data[0] ? formatPhoneNumber(data[0].phone_number) || "" : ""),            },
            {
              data: "owner_address",
              className: "text-gray-900",
              render: (data, type, row) => (data && data[0] ? data[0].street || "" : ""),
            },
            {
              data: "owner_address",
              className: "text-gray-900",
              render: (data, type, row) => (data && data[0] ? data[0].city || "" : ""),
            },
            {
              data: "owner_address",
              className: "text-gray-900",
              render: (data, type, row) => (data && data[0] ? data[0].state || "" : ""),
            },
          ],
        })
        
      } catch (error) {
        console.error("Error initializing Owners DataTable:", error)
      }
    }
  }

  const buildDataTablesParams = (dtParams, filters) => {
    

    const params = {
      page: Math.floor(dtParams.start / dtParams.length) + 1,
      per_page: dtParams.length,
      search: dtParams.search?.value || search || "",
    }

    // Handle sorting
    if (dtParams.order && dtParams.order.length > 0) {
      const orderColumn = dtParams.order[0]
      const columnData = dtParams.columns[orderColumn.column]?.data
      if (columnData && columnData !== null) {
        params.sort_by = columnData
        params.sort_direction = orderColumn.dir
      }
    }

    // Add filter parameters
    if (filters) {
      Object.keys(filters).forEach((key) => {
        if (filters[key] !== undefined && filters[key] !== "" && filters[key] !== null) {
          params[key] = filters[key]
        }
      })
    }

  
    return params
  }

  const fetchProperties = (filters) => {
    if (propertiesDataTableRef.current) {
      propertiesDataTableRef.current.ajax.reload()
    }
  }

  const fetchOwners = (filters) => {
    if (ownersDataTableRef.current) {
      ownersDataTableRef.current.ajax.reload()
    }
  }

  const onSuccess = (filters) => {
    setLoading(true)
    if (filters) {
      setFilterData(filters)
    }
    fetchProperties(filters || filterData)
    fetchOwners(filters || filterData)
    setLoading(false)
  }

  const onOwnerSuccess = (filters) => {
    setLoadingOwner(true)
    fetchOwners(filters || filterData)
    setLoadingOwner(false)
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    if (search) {
      queryParams.set("search", search)
    } else {
      queryParams.delete("search")
    }
    navigate({ search: queryParams.toString() }, { replace: true })
  }, [search, navigate, location.search])

  const selectedProperties =
    selectedItem.length !== 0 ? propertylistingdata?.filter((el) => selectedItem?.includes(el?.id)) : []

  // Handle select all for properties
  const handleSelectAllProperties = (isChecked) => {
    if (isChecked) {
      setSelectedItem([...propertylistingdata])
      window.$(".property-checkbox").prop("checked", true)
    } else {
      setSelectedItem([])
      window.$(".property-checkbox").prop("checked", false)
    }
  }

  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">
          Properties
          {selectedItem?.length > 0 && (
            <span className="body-L ml-2">
              ({selectedItem?.length}{" "}
              <span className="dark-H">{selectedItem?.length === 1 ? "item" : "items"} selected</span>)
            </span>
          )}
        </p>
        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 items-center">
            <PropertyFilter
              filterData={filterData}
              onSetFilterData={(value) => {
                setFilterData(value)
              }}
              onCallApiAgain={(filters) => onSuccess(filters)}
            />

            <ImportExport
              from="property"
              onCallApi={() => {
                onSuccess()
                setSelectedItem([])
              }}
              selectedData={selectedProperties}
            />
          </div>

          <div className="flex gap-4 justify-between items-center">
            <div className="search-box contacts">
              <input
                type="text"
                className="body-S px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Search property, owner, city, state..."
                value={search}
                onChange={(e) => {
                  const newValue = e.target.value
                  setSearch(newValue.startsWith(" ") ? newValue.trimStart() : newValue)
                  // Trigger DataTables search
                  if (propertiesDataTableRef.current) {
                    propertiesDataTableRef.current.search(newValue).draw()
                  }
                  if (ownersDataTableRef.current) {
                    ownersDataTableRef.current.search(newValue).draw()
                  }
                }}
              />
            </div>
            <button
              className="add-contact-button green-bg-H light-L body-S bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md flex items-center transition-colors"
              onClick={() => navigate(ROUTES.ADD_PROPERTY)}
            >
              <img className="mr-2 w-4 h-4" src={Plus || "/placeholder.svg"} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white pb-3 rounded-lg shadow-sm">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} p-4`}>
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                id="properties-select-all"
                className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                onChange={(e) => handleSelectAllProperties(e.target.checked)}
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Select All</span>
            </label>
          </div>

          <div className="overflow-x-auto">
            <table
              ref={propertiesTableRef}
              className="w-full text-sm text-left text-gray-500 bg-white border border-gray-200 rounded-lg"
            >
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th className="px-4 py-3">Select</th>
                  <th className="px-4 py-3">Property Name</th>
                  <th className="px-4 py-3">Property Type</th>
                  <th className="px-4 py-3">Property Address</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">State</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Last Contacted</th>
                  <th className="px-4 py-3">Last Update</th>
                  <th className="px-4 py-3">Tags</th>
                </tr>
              </thead>
              <tbody>{/* DataTables will populate this */}</tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="contact-header mt-8">
        <p className="head-1 dark-H pt-3">Owners</p>
      </div>

      <div className="bg-white pb-3 rounded-lg shadow-sm">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} p-4`}>
          <div className="overflow-x-auto">
            <table
              ref={ownersTableRef}
              className="w-full text-sm text-left text-gray-500 bg-white border border-gray-200 rounded-lg"
            >
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">State</th>
                </tr>
              </thead>
              <tbody>{/* DataTables will populate this */}</tbody>
            </table>
          </div>
        </div>
      </div>
    </BaseLayout>
  )
}

export default PropertiesDemo;
